/**
 * Worst-case / RSS / Monte Carlo, on one axis.
 *
 * PRD §4.5 makes showing the three side by side a feature: where they disagree,
 * the disagreement is the finding. A single shared x-axis is what makes that
 * legible — three separate scales would invent comparisons.
 *
 * Three categorical slots, each with a visible direct label. The labels are not
 * decoration: aqua sits below 3:1 on the light surface, so the palette's relief
 * rule requires identity to be readable without the swatch.
 */

import { useId } from 'react';
import type { OutputComparison } from '../../engine/analysis.ts';
import type { OutputResult } from '../../engine/montecarlo.ts';
import { eng, engAxis, niceTicks } from '../format.ts';
import { Info } from '../Info.tsx';

interface Props {
  comparison: OutputComparison;
  result: OutputResult;
}

const PAD = { top: 12, right: 20, bottom: 34, left: 118 };
const ROW_H = 34;

export function MethodCompare({ comparison, result }: Props) {
  const titleId = useId();
  const { wca, rss } = comparison;
  const unit = result.unit;

  const rows = [
    {
      key: 'wca',
      label: 'Worst case',
      lo: wca.min,
      hi: wca.max,
      colour: 'var(--series-1)',
      note: wca.monotonic ? `±${eng((wca.max - wca.min) / 2, 3, unit)}` : 'bound (non-monotonic)',
    },
    {
      key: 'rss',
      label: 'RSS ±3σ',
      lo: rss.nominal - 3 * rss.stdDev,
      hi: rss.nominal + 3 * rss.stdDev,
      colour: 'var(--series-2)',
      note: `σ = ${eng(rss.stdDev, 3, unit)}`,
    },
    {
      key: 'mc',
      label: 'Monte Carlo',
      lo: result.stats.mean - 3 * result.stats.stdDev,
      hi: result.stats.mean + 3 * result.stats.stdDev,
      colour: 'var(--series-3)',
      note: `σ = ${eng(result.stats.stdDev, 3, unit)}`,
      whiskerLo: result.stats.min,
      whiskerHi: result.stats.max,
    },
  ].filter((r) => Number.isFinite(r.lo) && Number.isFinite(r.hi));

  const values = rows.flatMap((r) => [r.lo, r.hi, r.whiskerLo ?? r.lo, r.whiskerHi ?? r.hi]);
  if (result.lsl !== undefined) values.push(result.lsl);
  if (result.usl !== undefined) values.push(result.usl);

  let lo = Math.min(...values.filter(Number.isFinite));
  let hi = Math.max(...values.filter(Number.isFinite));
  if (!(hi > lo)) {
    const eps = Math.abs(lo) * 1e-6 || 1e-6;
    lo -= eps;
    hi += eps;
  }
  const pad = (hi - lo) * 0.06;
  lo -= pad;
  hi += pad;

  const width = 720;
  const height = PAD.top + rows.length * ROW_H + PAD.bottom;
  const plotW = width - PAD.left - PAD.right;
  const x = (v: number) => PAD.left + ((v - lo) / (hi - lo)) * plotW;

  return (
    <figure className="chart">
      <figcaption id={titleId} className="chart-title">
        <span className="heading-text">Method comparison — {result.id} <Info topic="monte-carlo" /></span>
      </figcaption>

      <svg viewBox={`0 0 ${width} ${height}`} className="chart-svg" role="img" aria-labelledby={titleId}>
        {result.lsl !== undefined && (
          <rect
            x={PAD.left}
            y={PAD.top}
            width={Math.max(0, x(result.lsl) - PAD.left)}
            height={rows.length * ROW_H}
            fill="var(--status-critical)"
            opacity={0.06}
          />
        )}
        {result.usl !== undefined && (
          <rect
            x={x(result.usl)}
            y={PAD.top}
            width={Math.max(0, width - PAD.right - x(result.usl))}
            height={rows.length * ROW_H}
            fill="var(--status-critical)"
            opacity={0.06}
          />
        )}

        {[
          { v: result.lsl, label: 'LSL' },
          { v: result.usl, label: 'USL' },
        ]
          .filter((l): l is { v: number; label: string } => l.v !== undefined)
          .map((l) => (
            <line
              key={l.label}
              x1={x(l.v)}
              x2={x(l.v)}
              y1={PAD.top}
              y2={PAD.top + rows.length * ROW_H}
              stroke="var(--status-critical)"
              strokeWidth={2}
            />
          ))}

        {rows.map((r, i) => {
          const cy = PAD.top + i * ROW_H + ROW_H / 2;
          const inSpec =
            (result.lsl === undefined || r.lo >= result.lsl) &&
            (result.usl === undefined || r.hi <= result.usl);
          return (
            <g key={r.key}>
              {/* Direct label — identity never depends on the swatch alone */}
              <text
                x={PAD.left - 10}
                y={cy + 4}
                textAnchor="end"
                className="chart-label"
                fill="var(--text-secondary)"
              >
                {r.label}
              </text>

              {r.whiskerLo !== undefined && (
                <line
                  x1={x(r.whiskerLo)}
                  x2={x(r.whiskerHi!)}
                  y1={cy}
                  y2={cy}
                  stroke="var(--axis)"
                  strokeWidth={2}
                />
              )}

              <rect
                x={x(r.lo)}
                y={cy - 6}
                width={Math.max(2, x(r.hi) - x(r.lo))}
                height={12}
                rx={4}
                fill={r.colour}
              />

              <text
                x={Math.min(width - PAD.right, x(r.hi) + 8)}
                y={cy + 4}
                className="chart-tick"
                fill={inSpec ? 'var(--text-secondary)' : 'var(--status-critical)'}
              >
                {r.note}
              </text>
            </g>
          );
        })}

        <line
          x1={PAD.left}
          x2={width - PAD.right}
          y1={PAD.top + rows.length * ROW_H}
          y2={PAD.top + rows.length * ROW_H}
          stroke="var(--axis)"
        />
        {(() => {
          const ticks = niceTicks(lo, hi, 6);
          const label = engAxis(ticks, unit, 3);
          return ticks.map((t) => (
            <text
              key={t}
              x={x(t)}
              y={height - 12}
              textAnchor="middle"
              className="chart-tick"
              fill="var(--text-muted)"
            >
              {label(t)}
            </text>
          ));
        })()}
      </svg>

      <p className="chart-note">
        Worst case spans the full tolerance box; RSS and Monte Carlo are shown at ±3σ, with
        the thin rule marking the observed Monte Carlo extremes.
      </p>
    </figure>
  );
}
